import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  message,
  Space,
  Popconfirm,
  Tag,
  Card,
  Descriptions,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ReloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import { usePermissions } from '../../../context/permissionsContext';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

const Compras = () => {
  const { hasPermission } = usePermissions();
  const [compras, setCompras] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [articulos, setArticulos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalDetalleVisible, setModalDetalleVisible] = useState(false);
  const [compraSeleccionada, setCompraSeleccionada] = useState(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [detallesTemporal, setDetallesTemporal] = useState([]);
  const [articuloSeleccionado, setArticuloSeleccionado] = useState(null);
  const [cantidadTemporal, setCantidadTemporal] = useState(1);
  const [precioCompraTemporal, setPrecioCompraTemporal] = useState(0);
  const [formasPago, setFormasPago] = useState([]);
  const [formasPagoTemporal, setFormasPagoTemporal] = useState([]);
  const [formaPagoSeleccionada, setFormaPagoSeleccionada] = useState(null);
  const [montoFormaPago, setMontoFormaPago] = useState(0);
  const articuloSelectRef = useRef(null);
  const [fechaRango, setFechaRango] = useState([dayjs(), dayjs()]);
  const [saving, setSaving] = useState(false);

  const loadCompras = async (rangoOverride = null) => {
    setLoading(true);
    try {
      const rango = rangoOverride != null ? rangoOverride : fechaRango;
      const params = new URLSearchParams();
      if (rango && rango[0]) {
        params.append('fecha_desde', dayjs(rango[0]).format('YYYY-MM-DD'));
      }
      if (rango && rango[1]) {
        params.append('fecha_hasta', dayjs(rango[1]).format('YYYY-MM-DD'));
      }
      
      const response = await apiClient.get(`/gestion/compras?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setCompras(data);
      } else {
        message.error('Error al cargar las compras');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar las compras');
    } finally {
      setLoading(false);
    }
  };

  const loadProveedores = async () => {
    try {
      const response = await apiClient.get('/gestion/compras/recursos/proveedores');
      if (response.ok) {
        const data = await response.json();
        setProveedores(data || []);
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    }
  };

  const loadArticulos = async () => {
    try {
      const response = await apiClient.get('/gestion/compras/recursos/articulos');
      if (response.ok) {
        const data = await response.json();
        setArticulos(data || []);
      }
    } catch (error) {
      console.error('Error al cargar artículos:', error);
    }
  };

  const loadFormasPago = async () => {
    try {
      const response = await apiClient.get('/gestion/compras/recursos/formas-pago');
      if (response.ok) {
        const data = await response.json();
        setFormasPago(data || []);
      }
    } catch (error) {
      console.error('Error al cargar formas de pago:', error);
    }
  };

  useEffect(() => {
    loadCompras();
    loadProveedores();
    loadArticulos();
    loadFormasPago();
  }, []);

  // Cargar compras cuando cambia el filtro de fechas
  useEffect(() => {
    if (fechaRango && fechaRango[0] && fechaRango[1]) {
      loadCompras();
    }
  }, [fechaRango]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Limpiar formas de pago si el total del comprobante se elimina o es 0
  const totalComprobante = Form.useWatch('total_comprobante', form);
  useEffect(() => {
    if (modalVisible && (!totalComprobante || totalComprobante === 0)) {
      setFormasPagoTemporal([]);
      setFormaPagoSeleccionada(null);
      setMontoFormaPago(0);
    }
  }, [totalComprobante, modalVisible]);

  const handleOpenModal = () => {
    form.resetFields();
    form.setFieldsValue({
      fecha: dayjs(),
    });
    setDetallesTemporal([]);
    setArticuloSeleccionado(null);
    setCantidadTemporal(1);
    setPrecioCompraTemporal(0);
    setFormasPagoTemporal([]);
    setFormaPagoSeleccionada(null);
    setMontoFormaPago(0);
    setModalVisible(true);
  };

  const handleAgregarDetalle = () => {
    if (!articuloSeleccionado) {
      message.warning('Seleccione un artículo');
      return;
    }
    if (!cantidadTemporal || cantidadTemporal <= 0) {
      message.warning('Ingrese una cantidad válida');
      return;
    }
    if (!precioCompraTemporal || precioCompraTemporal <= 0) {
      message.warning('Ingrese un precio de compra válido');
      return;
    }

    const articulo = articulos.find(a => a.art_id === articuloSeleccionado);
    if (!articulo) {
      message.error('Artículo no encontrado');
      return;
    }

    // Verificar si el artículo ya está en los detalles
    if (detallesTemporal.some(d => d.articulo_id === articuloSeleccionado)) {
      message.warning('Este artículo ya está en la lista');
      return;
    }

    const precioUnitario = precioCompraTemporal;
    const nuevoDetalle = {
      articulo_id: articuloSeleccionado,
      articulo_nombre: articulo.art_nombre,
      cantidad: cantidadTemporal,
      precio_unitario: precioUnitario,
      subtotal: cantidadTemporal * precioUnitario,
    };

    setDetallesTemporal([...detallesTemporal, nuevoDetalle]);
    setArticuloSeleccionado(null);
    setCantidadTemporal(1);
    setPrecioCompraTemporal(0);
    // Devolver el foco al selector de artículo para seguir cargando
    setTimeout(() => articuloSelectRef.current?.focus(), 0);
  };

  const handleQuitarDetalle = (index) => {
    const nuevosDetalles = detallesTemporal.filter((_, i) => i !== index);
    setDetallesTemporal(nuevosDetalles);
  };

  const handleAgregarFormaPago = () => {
    if (!formaPagoSeleccionada) {
      message.warning('Seleccione una forma de pago');
      return;
    }
    if (!montoFormaPago || montoFormaPago <= 0) {
      message.warning('Ingrese un monto válido');
      return;
    }

    const formaPago = formasPago.find(fp => fp.fop_id === formaPagoSeleccionada);
    if (!formaPago) {
      message.error('Forma de pago no encontrada');
      return;
    }

    // Verificar si la forma de pago ya está agregada
    if (formasPagoTemporal.some(fp => fp.forma_pago_id === formaPagoSeleccionada)) {
      message.warning('Esta forma de pago ya está agregada');
      return;
    }

    const totalComprobante = form.getFieldValue('total_comprobante') || 0;
    const sumaActual = formasPagoTemporal.reduce((sum, fp) => sum + (fp.monto || 0), 0);
    const nuevaSuma = sumaActual + montoFormaPago;

    if (totalComprobante > 0 && nuevaSuma > totalComprobante + 0.01) {
      message.error(`La suma de las formas de pago (${nuevaSuma.toFixed(2)}) no puede exceder el total del comprobante (${totalComprobante.toFixed(2)})`);
      return;
    }

    setFormasPagoTemporal([...formasPagoTemporal, {
      forma_pago_id: formaPagoSeleccionada,
      forma_pago_nombre: formaPago.fop_nombre,
      monto: montoFormaPago
    }]);
    setFormaPagoSeleccionada(null);
    setMontoFormaPago(0);
  };

  const handleEliminarFormaPago = (index) => {
    const nuevasFormasPago = formasPagoTemporal.filter((_, i) => i !== index);
    setFormasPagoTemporal(nuevasFormasPago);
  };

  const handleSave = async () => {
    if (saving) return;
    try {
      setSaving(true);
      const values = await form.validateFields();
      
      if (detallesTemporal.length === 0) {
        message.error('Debe agregar al menos un artículo');
        return;
      }

      const totalComprobante = values.total_comprobante || 0;
      
      // Validar formas de pago si hay total_comprobante
      if (totalComprobante > 0) {
        if (formasPagoTemporal.length === 0) {
          message.error('Debe registrar al menos una forma de pago cuando hay total del comprobante');
          return;
        }
        
        const sumaFormasPago = formasPagoTemporal.reduce((sum, fp) => sum + (fp.monto || 0), 0);
        const diferencia = Math.abs(sumaFormasPago - totalComprobante);
        
        if (diferencia > 0.01) {
          message.error(`La suma de las formas de pago (${sumaFormasPago.toFixed(2)}) debe ser igual al total del comprobante (${totalComprobante.toFixed(2)})`);
          return;
        }
      }

      const dataToSend = {
        proveedor_id: values.proveedor_id,
        fecha: values.fecha.format('YYYY-MM-DD'),
        observaciones: values.observaciones || null,
        total_comprobante: values.total_comprobante || null,
        detalles: detallesTemporal.map(d => ({
          articulo_id: d.articulo_id,
          cantidad: d.cantidad,
          precio_unitario: d.precio_unitario,
        })),
        formas_pago: formasPagoTemporal.map(fp => ({
          forma_pago_id: fp.forma_pago_id,
          monto: fp.monto
        }))
      };

      const response = await apiClient.post('/gestion/compras', dataToSend);
      if (response.ok) {
        message.success('Compra registrada correctamente');
        setModalVisible(false);
        form.resetFields();
        setDetallesTemporal([]);
        setArticuloSeleccionado(null);
        setCantidadTemporal(1);
        setPrecioCompraTemporal(0);
        setFormasPagoTemporal([]);
        setFormaPagoSeleccionada(null);
        setMontoFormaPago(0);
        loadCompras();
        loadArticulos(); // Recargar artículos para actualizar stock y precios
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al registrar la compra');
      }
    } catch (error) {
      if (error.errorFields) {
        // Mostrar el primer error de validación
        const firstError = error.errorFields?.[0];
        if (firstError?.errors?.[0]) {
          message.error(firstError.errors[0]);
        } else {
          message.error('Por favor complete todos los campos requeridos');
        }
        return;
      }
      console.error('Error:', error);
      message.error('Error al guardar la compra');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/gestion/compras/${id}`);
      if (response.ok) {
        message.success('Compra eliminada correctamente');
        loadCompras();
        loadArticulos();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar la compra');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar la compra');
    }
  };

  const handleVerDetalle = (compra) => {
    setCompraSeleccionada(compra);
    setModalDetalleVisible(true);
  };

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'com_fecha',
      key: 'com_fecha',
      render: (fecha) => dayjs(fecha).format('DD/MM/YYYY'),
    },
    {
      title: 'Proveedor',
      dataIndex: 'proveedor_nombre',
      key: 'proveedor_nombre',
    },
    {
      title: 'Total',
      dataIndex: 'com_total_comprobante',
      key: 'com_total_comprobante',
      render: (total) => {
        if (total == null || total === undefined) {
          return '-';
        }
        return `$${parseFloat(total).toFixed(2)}`;
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 200,
      render: (_, record) => (
        <Space>
          {hasPermission('compras', 'ver_compra') && (
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleVerDetalle(record)}
            >
              Ver
            </Button>
          )}
          {hasPermission('compras', 'eliminar') && (
            <Popconfirm
              title="¿Estás seguro de eliminar esta compra?"
              onConfirm={() => handleDelete(record.com_id)}
              okText="Sí"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                Eliminar
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>
            Gestión de Compras
          </h1>
          <Space wrap style={{ marginBottom: 16 }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadCompras}
              loading={loading}
            >
              {isMobile ? null : 'Actualizar'}
            </Button>
            {hasPermission('compras', 'crear') && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOpenModal}
              >
                Nueva Compra
              </Button>
            )}
          </Space>
          <div style={{
            padding: 12,
            backgroundColor: '#2d2d2d',
            borderRadius: 8,
            marginBottom: 16,
            borderLeft: '4px solid #1890ff'
          }}>
            <p style={{ margin: 0, color: '#b0b0b0', fontSize: '13px' }}>
              <strong style={{ color: '#ffffff' }}>Nota:</strong> Solo se muestran artículos que <strong>mueven stock</strong> 
              (productos físicos). Para artículos sin stock, use el módulo de Gastos.
            </p>
          </div>

          {/* Filtros */}
          <div style={{
            marginBottom: 16,
            padding: 16,
            backgroundColor: '#2d2d2d',
            borderRadius: 8,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 12,
            alignItems: isMobile ? 'stretch' : 'flex-end'
          }}>
            <div style={{ flex: 1, minWidth: isMobile ? '100%' : '300px' }}>
              <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                Rango de fechas
              </label>
              <RangePicker
                value={fechaRango}
                onChange={(dates) => setFechaRango(dates)}
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={compras}
          loading={loading}
          rowKey="com_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} compras`,
          }}
        />

        {/* Modal para crear compra */}
        <Modal
          title="Nueva Compra"
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
            setDetallesTemporal([]);
            setArticuloSeleccionado(null);
            setCantidadTemporal(1);
            setPrecioCompraTemporal(0);
            setFormasPagoTemporal([]);
            setFormaPagoSeleccionada(null);
            setMontoFormaPago(0);
          }}
          okText="Registrar"
          cancelText="Cancelar"
          confirmLoading={saving}
          width={800}
        >
          <Form form={form} layout="vertical">
            <div style={{ display: 'flex', gap: 16 }}>
              <Form.Item
                name="proveedor_id"
                label="Proveedor"
                rules={[{ required: true, message: 'Seleccione un proveedor' }]}
                style={{ flex: 1 }}
              >
                <Select 
                  placeholder="Seleccione un proveedor" 
                  showSearch
                  filterOption={(input, option) => {
                    const text = String(option?.children || option?.label || '');
                    return text.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {proveedores.map(prov => (
                    <Select.Option key={prov.pro_id} value={prov.pro_id}>
                      {prov.pro_nombre}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="fecha"
                label="Fecha"
                rules={[{ required: true, message: 'Seleccione la fecha' }]}
                style={{ flex: 1 }}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </div>

            {/* Total del Comprobante y Observaciones lado a lado */}
            <div style={{ display: 'flex', gap: 16 }}>
              {/* Total del Comprobante */}
              <Form.Item
                name="total_comprobante"
                label="Total del Comprobante"
                normalize={(value) => {
                  // Si ya es un número válido, devolverlo
                  if (typeof value === 'number' && !isNaN(value) && value >= 0) {
                    return value;
                  }
                  // Si es null/undefined/vacío, devolver undefined
                  if (!value || value === '$' || (typeof value === 'string' && value.trim() === '')) {
                    return undefined;
                  }
                  // Si es string formateado, parsearlo
                  let cleaned = String(value).replace(/\$\s?/g, '').trim();
                  if (!cleaned) {
                    return undefined;
                  }
                  cleaned = cleaned.replace(/\./g, '');
                  cleaned = cleaned.replace(',', '.');
                  const num = parseFloat(cleaned);
                  if (!isNaN(num) && num >= 0) {
                    return num;
                  }
                  return undefined;
                }}
                rules={[
                  { required: true, message: 'Ingrese el total del comprobante' },
                  { type: 'number', min: 0, message: 'El total debe ser mayor o igual a 0' }
                ]}
                style={{ flex: 1 }}
              >
                <div
                  style={{
                    padding: '0 12px',
                    backgroundColor: '#1a1a1a',
                    borderRadius: 6,
                    borderLeft: '4px solid #52c41a',
                    color: '#52c41a',
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: '32px'
                  }}
                  className="total-comprobante-verde"
                >
                  <InputNumber
                    min={0}
                    step={0.01}
                    formatter={(value) => {
                      if (value == null || value === '') return '';
                      const parts = String(value).split('.');
                      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                      return `$ ${parts.join(',')}`;
                    }}
                    parser={(value) => {
                      if (!value || value === '$' || (typeof value === 'string' && value.trim() === '')) {
                        return undefined;
                      }
                      // Si ya es un número, devolverlo directamente
                      if (typeof value === 'number') {
                        return value >= 0 ? value : undefined;
                      }
                      // Remover símbolo de peso y espacios
                      let cleaned = String(value).replace(/\$\s?/g, '').trim();
                      if (!cleaned || cleaned === '') {
                        return undefined;
                      }
                      // Remover puntos (separadores de miles)
                      cleaned = cleaned.replace(/\./g, '');
                      // Reemplazar coma por punto (decimal)
                      cleaned = cleaned.replace(',', '.');
                      const num = parseFloat(cleaned);
                      if (isNaN(num) || num < 0) {
                        return undefined;
                      }
                      return num;
                    }}
                    placeholder="Ingrese el total de la factura (ej: 1.234,56)"
                    style={{ width: '100%', fontSize: '16px' }}
                    size="large"
                    variant="borderless"
                  />
                </div>
              </Form.Item>

              <Form.Item 
                name="observaciones" 
                label="Observaciones"
                style={{ flex: 1 }}
              >
                <TextArea rows={2} placeholder="Observaciones (opcional)" />
              </Form.Item>
            </div>

            {/* Formas de Pago */}
            {form.getFieldValue('total_comprobante') > 0 && (
              <>
                <Divider>Formas de Pago</Divider>
                
                {/* Campos para agregar forma de pago */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'flex-end' }}>
                  <div style={{ flex: 2 }}>
                    <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                      Forma de Pago
                    </label>
                    <Select
                      placeholder="Seleccione una forma de pago"
                      value={formaPagoSeleccionada}
                      onChange={(value) => setFormaPagoSeleccionada(value)}
                      showSearch
                      style={{ width: '100%' }}
                      filterOption={(input, option) => {
                        const text = String(option?.children || option?.label || '');
                        return text.toLowerCase().includes(input.toLowerCase());
                      }}
                    >
                      {formasPago
                        .filter(fp => !formasPagoTemporal.some(fpt => fpt.forma_pago_id === fp.fop_id))
                        .map(fp => (
                        <Select.Option key={fp.fop_id} value={fp.fop_id}>
                          {fp.fop_nombre}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                      Monto
                    </label>
                    <InputNumber
                      min={0}
                      step={0.01}
                      value={montoFormaPago}
                      onChange={(value) => setMontoFormaPago(value || 0)}
                      formatter={(value) => {
                        if (value == null || value === '') return '';
                        const parts = String(value).split('.');
                        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                        return `$ ${parts.join(',')}`;
                      }}
                      parser={(value) => {
                        if (!value || value === '$' || (typeof value === 'string' && value.trim() === '')) {
                          return 0;
                        }
                        let cleaned = String(value).replace(/\$\s?/g, '').trim();
                        if (!cleaned || cleaned === '') {
                          return 0;
                        }
                        cleaned = cleaned.replace(/\./g, '');
                        cleaned = cleaned.replace(',', '.');
                        const num = parseFloat(cleaned);
                        return isNaN(num) || num < 0 ? 0 : num;
                      }}
                      style={{ width: '100%' }}
                      placeholder="Monto"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAgregarFormaPago();
                        }
                      }}
                    />
                  </div>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAgregarFormaPago}
                    style={{ height: '32px' }}
                  >
                    Agregar
                  </Button>
                </div>

                {/* Grilla de formas de pago agregadas */}
                {formasPagoTemporal.length > 0 && (
                  <>
                    <Table
                      dataSource={formasPagoTemporal}
                      rowKey={(record, index) => index}
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: 'Forma de Pago',
                          dataIndex: 'forma_pago_nombre',
                          key: 'forma_pago_nombre',
                        },
                        {
                          title: 'Monto',
                          dataIndex: 'monto',
                          key: 'monto',
                          render: (monto) => `$${parseFloat(monto).toFixed(2)}`,
                          align: 'right',
                        },
                        {
                          title: 'Acciones',
                          key: 'acciones',
                          width: 100,
                          render: (_, record, index) => (
                            <Button
                              type="link"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => handleEliminarFormaPago(index)}
                            >
                              Quitar
                            </Button>
                          ),
                        },
                      ]}
                    />
                    <div style={{ marginTop: 8, textAlign: 'right', fontWeight: 'bold', color: '#ffffff' }}>
                      Total Formas de Pago: ${formasPagoTemporal.reduce((sum, fp) => sum + (fp.monto || 0), 0).toFixed(2)}
                      {form.getFieldValue('total_comprobante') > 0 && (
                        <span style={{ marginLeft: 16, color: Math.abs(formasPagoTemporal.reduce((sum, fp) => sum + (fp.monto || 0), 0) - form.getFieldValue('total_comprobante')) <= 0.01 ? '#52c41a' : '#ff4d4f' }}>
                          {Math.abs(formasPagoTemporal.reduce((sum, fp) => sum + (fp.monto || 0), 0) - form.getFieldValue('total_comprobante')) <= 0.01 ? '✓' : '✗'}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </>
            )}

            <Divider>Detalles de la Compra</Divider>

            {/* Campos para agregar detalle */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'flex-end' }}>
              <div style={{ flex: 2 }}>
                <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                  Artículo
                </label>
                <Select
                  id="articulo-select"
                  ref={articuloSelectRef}
                  placeholder="Seleccione un artículo"
                  value={articuloSeleccionado}
                  onChange={(value) => {
                    setArticuloSeleccionado(value);
                    const articulo = articulos.find(a => a.art_id === value);
                    if (articulo) {
                      setPrecioCompraTemporal(articulo.art_precio_compra || 0);
                    }
                  }}
                  showSearch
                  style={{ width: '100%' }}
                  filterOption={(input, option) => {
                    const text = String(option?.children || option?.label || '');
                    return text.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {articulos
                    .filter(art => !detallesTemporal.some(d => d.articulo_id === art.art_id))
                    .map(art => (
                    <Select.Option key={art.art_id} value={art.art_id}>
                      {art.art_nombre} - Stock: {art.art_stock_actual}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                  Cantidad
                </label>
                <InputNumber
                  id="cantidad-input"
                  min={1}
                  value={cantidadTemporal}
                  onChange={(value) => setCantidadTemporal(value || 1)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAgregarDetalle();
                    }
                  }}
                  style={{ width: '100%' }}
                  placeholder="Cant."
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                  Precio Compra
                </label>
                <InputNumber
                  id="precio-compra-input"
                  min={0}
                  step={0.01}
                  value={precioCompraTemporal}
                  onChange={(value) => setPrecioCompraTemporal(value || 0)}
                  formatter={(value) => {
                    if (value == null || value === '') return '';
                    const parts = String(value).split('.');
                    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                    return `$ ${parts.join(',')}`;
                  }}
                  parser={(value) => {
                    if (!value || value === '$' || (typeof value === 'string' && value.trim() === '')) {
                      return 0;
                    }
                    let cleaned = String(value).replace(/\$\s?/g, '').trim();
                    if (!cleaned || cleaned === '') {
                      return 0;
                    }
                    cleaned = cleaned.replace(/\./g, '');
                    cleaned = cleaned.replace(',', '.');
                    const num = parseFloat(cleaned);
                    return isNaN(num) || num < 0 ? 0 : num;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAgregarDetalle();
                    }
                  }}
                  style={{ width: '100%' }}
                  placeholder="Precio"
                />
              </div>
              <Button
                id="agregar-button"
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAgregarDetalle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAgregarDetalle();
                  }
                }}
                style={{ height: '32px' }}
              >
                Agregar
              </Button>
            </div>

            {/* Grilla de artículos agregados */}
            {detallesTemporal.length > 0 && (
              <Table
                dataSource={detallesTemporal}
                rowKey={(record) => record.articulo_id}
                pagination={false}
                size="small"
                columns={[
                  {
                    title: 'Artículo',
                    dataIndex: 'articulo_nombre',
                    key: 'articulo_nombre',
                  },
                  {
                    title: 'Cantidad',
                    dataIndex: 'cantidad',
                    key: 'cantidad',
                    width: 100,
                    align: 'center',
                  },
                  {
                    title: 'Precio Unitario',
                    dataIndex: 'precio_unitario',
                    key: 'precio_unitario',
                    width: 120,
                    align: 'right',
                    render: (precio) => `$${parseFloat(precio).toFixed(2)}`,
                  },
                  {
                    title: 'Subtotal',
                    dataIndex: 'subtotal',
                    key: 'subtotal',
                    width: 120,
                    align: 'right',
                    render: (subtotal) => `$${parseFloat(subtotal).toFixed(2)}`,
                  },
                  {
                    title: 'Acción',
                    key: 'accion',
                    width: 80,
                    align: 'center',
                    render: (_, record, index) => (
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleQuitarDetalle(index)}
                        size="small"
                      >
                        Quitar
                      </Button>
                    ),
                  },
                ]}
              />
            )}
          </Form>
        </Modal>

        {/* Modal para ver detalle */}
        <Modal
          title="Detalle de Compra"
          open={modalDetalleVisible}
          onCancel={() => setModalDetalleVisible(false)}
          footer={null}
          width={600}
        >
          {compraSeleccionada && (
            <>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Fecha">
                  {dayjs(compraSeleccionada.com_fecha).format('DD/MM/YYYY')}
                </Descriptions.Item>
                <Descriptions.Item label="Proveedor">
                  {compraSeleccionada.proveedor_nombre}
                </Descriptions.Item>
                <Descriptions.Item label="Total">
                  {compraSeleccionada.com_total_comprobante != null 
                    ? `$${parseFloat(compraSeleccionada.com_total_comprobante).toFixed(2)}`
                    : compraSeleccionada.com_total != null
                    ? `$${parseFloat(compraSeleccionada.com_total).toFixed(2)}`
                    : '-'
                  }
                </Descriptions.Item>
                {compraSeleccionada.com_observaciones && (
                  <Descriptions.Item label="Observaciones">
                    {compraSeleccionada.com_observaciones}
                  </Descriptions.Item>
                )}
              </Descriptions>
              
              {/* Formas de Pago */}
              {compraSeleccionada.formas_pago && compraSeleccionada.formas_pago.length > 0 && (
                <>
                  <Divider>Formas de Pago</Divider>
                  <Table
                    columns={[
                      { title: 'Forma de Pago', dataIndex: 'forma_pago_nombre', key: 'forma_pago_nombre' },
                      { 
                        title: 'Monto', 
                        dataIndex: 'cfp_monto', 
                        key: 'cfp_monto',
                        align: 'right',
                        render: (monto) => `$${parseFloat(monto).toFixed(2)}`
                      },
                    ]}
                    dataSource={compraSeleccionada.formas_pago}
                    pagination={false}
                    size="small"
                    summary={() => (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} align="right">
                            <strong>Total:</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="right">
                            <strong>
                              ${compraSeleccionada.formas_pago.reduce((sum, fp) => sum + (parseFloat(fp.cfp_monto) || 0), 0).toFixed(2)}
                            </strong>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    )}
                  />
                </>
              )}
              
              <Divider>Artículos</Divider>
              <Table
                columns={[
                  { title: 'Artículo', dataIndex: 'articulo_nombre', key: 'articulo_nombre' },
                  { title: 'Cantidad', dataIndex: 'cod_cantidad', key: 'cod_cantidad', align: 'center' },
                ]}
                dataSource={compraSeleccionada.detalles || []}
                pagination={false}
                size="small"
              />
            </>
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default Compras;
